import React, { useState, useEffect } from 'react';
import {
  Monitor, Laptop, Cpu, Server, ScreenShare, BadgeCheck,
  Wrench, ArrowRight, ExternalLink, ShieldCheck, Phone, Star,
  MessageCircle, ChevronDown, ChevronUp, PlayCircle, Newspaper, ShieldAlert,
} from 'lucide-react';

// ── Identidade visual Daten ──────────────────────────────────────────
// Cor primária: #1d3557 (navy Daten) · Acento: #457b9d
const DATEN_NAVY = '#1d3557';
const DATEN_BLUE = '#457b9d';

const WA_PHONE = '5516991378312';

// Mensagens WhatsApp específicas por CTA
const WA_MSGS = {
  quote:
    'Olá, Fernando Tech! Acessei a seção DATEN no site e gostaria de solicitar uma cotação. O equipamento ou solução que procuro é:',
  support:
    'Olá, Fernando Tech! Encontrei vocês pela seção DATEN e preciso de atendimento técnico para um equipamento DATEN. Modelo, número de série e problema:',
  products:
    'Olá, Fernando Tech! Gostaria de receber informações sobre os produtos DATEN disponíveis para cotação.',
  specialist:
    'Olá, Fernando Tech! Quero falar com um especialista sobre soluções DATEN para minha empresa.',
  category: (categoria) =>
    `Olá, Fernando Tech! Acessei a seção DATEN no site e gostaria de solicitar uma cotação de ${categoria}. Detalhes/uso:`,
};

const buildWA = (msg) => `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(msg)}`;

// Dispara evento GA4 se disponível (não instala nada — usa gtag existente)
const track = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...params });
  }
};

// ── Produtos DATEN (resumos autorais — fonte: daten.com.br) ─────────
const PRODUCTS = [
  {
    id: 'notebooks',
    title: 'Notebooks',
    subtitle: 'Mobilidade e Performance',
    icon: Laptop,
    image:
      'https://www.daten.com.br/novo_site/imagens/ads/35c479ee2b3dc1a7acca50cbd5e83f600524c479ee2b3dc1a7acca50cbd5e83f6005note.png',
    shortDesc:
      'Linha de notebooks corporativos com foco em produtividade, mobilidade e uso empresarial.',
    benefit: 'Ideal para empresas, órgãos públicos e uso profissional.',
    link: 'https://www.daten.com.br/setor-publico.php?c=11#11a',
  },
  {
    id: 'desktops',
    title: 'Desktops Corporativos',
    subtitle: 'Robustez Corporativa',
    icon: Monitor,
    image:
      'https://www.daten.com.br/files/46c479ee2b3dc1a7acca50cbd5e83f600537c479ee2b3dc1a7acca50cbd5e83f6005desk1_gov.png',
    shortDesc:
      'Desktops robustos para uso corporativo e governamental, com configurações sob demanda.',
    benefit: 'Alta durabilidade e reposição de peças garantida.',
    link: 'https://www.daten.com.br/setor-publico.php?c=6#6a',
  },
  {
    id: 'minipc',
    title: 'Mini PCs',
    subtitle: 'Compacto e Silencioso',
    icon: Cpu,
    image: 'https://www.daten.com.br/novo_site/imagens/ads/52c479ee2b3dc1a7acca50cbd5e83f6005mini.png',
    shortDesc:
      'Mini PCs de tamanho reduzido, baixo ruído e desempenho para ambientes corporativos.',
    benefit: 'Perfeitos para salas de reunião, quiosques e recepções.',
    link: 'https://www.daten.com.br/setor-publico.php?c=7#7a',
  },
  {
    id: 'allinone',
    title: 'All in One',
    subtitle: 'CPU integrada ao monitor',
    icon: ScreenShare,
    image:
      'https://www.daten.com.br/novo_site/imagens/ads/48c479ee2b3dc1a7acca50cbd5e83f600543c479ee2b3dc1a7acca50cbd5e83f6005aio.png',
    shortDesc:
      'Computadores All in One com CPU integrada ao monitor — sem cabos soltos.',
    benefit: 'Excelente para balcões, PDVs e escritórios organizados.',
    link: 'https://www.daten.com.br/setor-publico.php?c=3#3a',
  },
  {
    id: 'monitores',
    title: 'Monitores',
    subtitle: 'Clareza e Produtividade',
    icon: Monitor,
    image: 'https://www.daten.com.br/files/55c479ee2b3dc1a7acca50cbd5e83f6005monitor_2.png',
    shortDesc:
      'Monitores Full HD e QHD com painel IPS, ideais para estações de trabalho.',
    benefit: 'Cores fiéis, suporte VESA e diversas conexões.',
    link: 'https://www.daten.com.br/setor-publico.php?c=15#15a',
  },
  {
    id: 'servidores',
    title: 'Servidores e Soluções Empresariais',
    subtitle: 'Infraestrutura Confiável',
    icon: Server,
    image: 'https://www.daten.com.br/files/15c479ee2b3dc1a7acca50cbd5e83f6005Sem-Titulo-1.png',
    shortDesc:
      'Servidores para pequenas e médias empresas com suporte a virtualização e redundância.',
    benefit: 'Adequados a ERPs, sistemas críticos e ambientes corporativos.',
    link: 'https://www.daten.com.br/setor-publico.php?c=14#14a',
  },
];

// ── Novidades DATEN (fonte: daten.com.br e loja.daten.com.br) ───────
const CONSULTED_AT = 'Consultado em 2026';
const NEWS = [
  {
    id: 'secure-boot',
    icon: ShieldAlert,
    title: 'Atualização dos certificados Windows Secure Boot em 2026',
    excerpt:
      'A Microsoft está atualizando os certificados do Secure Boot ao longo de 2026. Determinados computadores podem precisar de atualização de BIOS/firmware para receberem os novos certificados. Consulte a Fernando Tech para uma avaliação técnica do seu equipamento DATEN.',
    date: CONSULTED_AT,
    link: 'https://daten.com.br/secure-boot',
    highlight: true,
  },
  {
    id: 'linha-corporativa',
    icon: Newspaper,
    title: 'Linha DATEN corporativa e para setor público',
    excerpt:
      'A DATEN mantém linhas específicas para o setor público e mercado corporativo, com equipamentos homologados para licitações e projetos empresariais.',
    date: CONSULTED_AT,
    link: 'https://www.daten.com.br/',
  },
  {
    id: 'loja-oficial',
    icon: Newspaper,
    title: 'Loja oficial DATEN',
    excerpt:
      'Consulte a loja oficial DATEN para conhecer disponibilidade, garantia e informações atualizadas dos produtos.',
    date: CONSULTED_AT,
    link: 'https://loja.daten.com.br/',
  },
];

// ── FAQ visível ────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'Onde encontrar assistência técnica DATEN em Ribeirão Preto?',
    a: 'A Fernando Tech realiza atendimento a equipamentos DATEN em Ribeirão Preto e região. Fale conosco pelo WhatsApp (16) 99137-8312 para agendar seu atendimento.',
  },
  {
    q: 'A Fernando Tech realiza atendimento em equipamentos DATEN?',
    a: 'Sim. A Fernando Tech está cadastrada como Assistência Técnica Autorizada DATEN, atendendo empresas, órgãos públicos e usuários finais em Ribeirão Preto e região.',
  },
  {
    q: 'Posso solicitar cotação de computadores DATEN pelo WhatsApp?',
    a: 'Sim. Basta clicar em qualquer botão “Cotar computador DATEN” nesta seção. Uma mensagem já é aberta pronta para você enviar pelo WhatsApp.',
  },
  {
    q: 'Quais linhas de produtos DATEN podem ser consultadas?',
    a: 'Notebooks, desktops corporativos, mini PCs, computadores all in one, monitores e servidores e soluções empresariais — todos com atendimento e cotação pela Fernando Tech.',
  },
  {
    q: 'Como saber se meu computador DATEN precisa atualizar o Secure Boot?',
    a: 'A DATEN publicou orientação oficial em daten.com.br/secure-boot. Nossa equipe pode avaliar o seu equipamento e orientar sobre atualização de BIOS e certificados. Envie modelo e número de série pelo WhatsApp.',
  },
];

// ── Componente ─────────────────────────────────────────────────────
const Daten = () => {
  const [activeTab, setActiveTab] = useState('notebooks');
  const [openFaq, setOpenFaq] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const active = PRODUCTS.find(p => p.id === activeTab);
  const ActiveIcon = active.icon;

  // Injeta JSON-LD específico da seção (Service + FAQPage + VideoObject + ItemList)
  useEffect(() => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': 'https://fernandotech.com.br/#daten-service',
          name: 'Assistência Técnica Autorizada DATEN — Fernando Tech',
          serviceType: 'Assistência técnica e cotação de equipamentos DATEN',
          areaServed: { '@type': 'City', name: 'Ribeirão Preto' },
          provider: { '@id': 'https://fernandotech.com.br/#organization-extended' },
          description:
            'Assistência técnica e cotação de notebooks, desktops, mini PCs, all in one, monitores e servidores DATEN em Ribeirão Preto e região.',
          url: 'https://fernandotech.com.br/daten',
        },
        {
          '@type': 'FAQPage',
          '@id': 'https://fernandotech.com.br/#daten-faq',
          mainEntity: FAQS.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@type': 'VideoObject',
          '@id': 'https://fernandotech.com.br/#daten-video',
          name: 'Conheça a tecnologia DATEN',
          description:
            'Vídeo institucional DATEN incorporado na seção DATEN do site Fernando Tech.',
          embedUrl: 'https://www.youtube-nocookie.com/embed/IGPF4l8HVSI?start=8',
          uploadDate: '2019-07-30T10:00:00-03:00',
          publisher: { '@id': 'https://fernandotech.com.br/#organization-extended' },
          thumbnailUrl: 'https://img.youtube.com/vi/IGPF4l8HVSI/hqdefault.jpg',
        },
        {
          '@type': 'ItemList',
          '@id': 'https://fernandotech.com.br/#daten-itemlist',
          name: 'Categorias de produtos DATEN cotadas pela Fernando Tech',
          itemListElement: PRODUCTS.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `${p.title} DATEN`,
            url: p.link,
          })),
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-daten-section';
    script.text = JSON.stringify(jsonLd);
    // Evita duplicar
    const existing = document.getElementById('ld-daten-section');
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('ld-daten-section');
      if (el) el.remove();
    };
  }, []);

  const CTAButton = ({ msgKey, label, icon: Icon = MessageCircle, eventName, testId, extraParams = {}, variant = 'primary' }) => (
    <a
      href={buildWA(WA_MSGS[msgKey] || WA_MSGS.quote)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track(eventName, extraParams)}
      className={
        variant === 'primary'
          ? 'inline-flex items-center gap-2 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-all shadow-lg hover:opacity-90'
          : 'inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-all border'
      }
      style={
        variant === 'primary'
          ? { background: '#25D366' }
          : { background: 'rgba(255,255,255,0.06)', color: '#93c5fd', borderColor: `${DATEN_BLUE}60` }
      }
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
      {label}
    </a>
  );

  return (
    <section
      id="daten"
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1d3557 40%, #0d1b2a 100%)' }}
      aria-labelledby="daten-heading"
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      {/* microdata invisível para SEO local */}
      <meta itemProp="name" content="Fernando Tech — Assistência Técnica Autorizada DATEN" />
      <meta itemProp="description" content="Produtos e Assistência Técnica Autorizada DATEN em Ribeirão Preto — Fernando Tech." />
      <link itemProp="url" href="https://fernandotech.com.br/#daten" />
      <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
        <meta itemProp="addressLocality" content="Ribeirão Preto" />
        <meta itemProp="addressRegion" content="SP" />
        <meta itemProp="addressCountry" content="BR" />
      </div>

      {/* Glow */}
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: DATEN_BLUE }} />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full filter blur-3xl" style={{ background: DATEN_NAVY }} />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-6xl">

        {/* ── HEADER / APRESENTAÇÃO ───────────────────────────────── */}
        <header className="text-center mb-10">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm">
              <BadgeCheck className="w-4 h-4 text-green-400" /> Assistência Técnica Autorizada
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm">
              <Star className="w-4 h-4 text-yellow-400" /> Cotação empresarial e pessoal
            </span>
          </div>

          <div
            className="text-4xl sm:text-5xl md:text-6xl font-black tracking-widest select-none mb-3"
            style={{ color: '#fff', fontStyle: 'italic', textShadow: '0 0 40px rgba(69,123,157,0.6)' }}
            aria-label="DATEN"
          >
            DATEN
          </div>

          <h2
            id="daten-heading"
            className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-4 leading-tight max-w-3xl mx-auto"
          >
            Produtos e Assistência Técnica Autorizada DATEN em{' '}
            <span className="text-blue-300">Ribeirão Preto</span>
          </h2>

          <p className="text-blue-200 max-w-2xl mx-auto text-sm sm:text-base mb-6 leading-relaxed">
            A <strong>Fernando Tech</strong> oferece atendimento especializado para equipamentos DATEN em
            Ribeirão Preto e região. Solicite assistência técnica, informações sobre produtos e cotações de
            computadores para empresas, instituições e profissionais.
          </p>

          {/* CTA 1 — após apresentação */}
          <div className="flex flex-wrap justify-center gap-3">
            <CTAButton
              msgKey="quote"
              label="Cotar computador DATEN"
              icon={MessageCircle}
              eventName="daten_whatsapp_quote"
              extraParams={{ location: 'header' }}
              testId="daten-cta-quote-header"
            />
            <CTAButton
              msgKey="support"
              label="Solicitar assistência DATEN"
              icon={Wrench}
              eventName="daten_whatsapp_support"
              extraParams={{ location: 'header' }}
              testId="daten-cta-support-header"
              variant="secondary"
            />
          </div>
        </header>

        {/* ── VÍDEO DATEN (lazy, responsivo, nocookie, start=8) ───── */}
        <section className="mb-14" aria-labelledby="daten-video-heading">
          <h3
            id="daten-video-heading"
            className="text-center text-white text-lg sm:text-xl font-bold mb-4"
          >
            Conheça a tecnologia DATEN
          </h3>
          <div
            className="relative w-full mx-auto rounded-2xl overflow-hidden border shadow-2xl"
            style={{ maxWidth: '900px', aspectRatio: '16 / 9', background: '#000', borderColor: `${DATEN_BLUE}40` }}
          >
            {!videoLoaded ? (
              <button
                type="button"
                onClick={() => {
                  setVideoLoaded(true);
                  track('daten_video_click', { video_id: 'IGPF4l8HVSI' });
                }}
                aria-label="Reproduzir vídeo: Conheça a tecnologia DATEN"
                className="absolute inset-0 w-full h-full flex items-center justify-center group cursor-pointer"
                style={{
                  backgroundImage: 'url(https://img.youtube.com/vi/IGPF4l8HVSI/hqdefault.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="daten-video-play"
              >
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />
                <PlayCircle className="w-20 h-20 sm:w-24 sm:h-24 text-white relative z-10 drop-shadow-2xl group-hover:scale-110 transition-transform" />
              </button>
            ) : (
              <iframe
                title="Conheça a tecnologia DATEN — vídeo institucional DATEN"
                src="https://www.youtube-nocookie.com/embed/IGPF4l8HVSI?start=8&autoplay=1&rel=0"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            )}
          </div>
          <div className="text-center mt-5">
            <CTAButton
              msgKey="quote"
              label="Solicitar cotação pelo WhatsApp"
              icon={MessageCircle}
              eventName="daten_whatsapp_quote"
              extraParams={{ location: 'video' }}
              testId="daten-cta-quote-video"
            />
          </div>
        </section>

        {/* ── PRODUTOS E SOLUÇÕES ─────────────────────────────────── */}
        <section aria-labelledby="daten-products-heading" className="mb-12">
          <h3
            id="daten-products-heading"
            className="text-center text-white text-lg sm:text-xl font-bold mb-6"
          >
            Produtos e soluções DATEN que cotamos
          </h3>

          {/* Tabs de categoria */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {PRODUCTS.map(p => {
              const Icon = p.icon;
              const isActive = activeTab === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveTab(p.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200"
                  style={isActive
                    ? { background: DATEN_BLUE, color: '#fff', boxShadow: `0 4px 20px ${DATEN_BLUE}60` }
                    : { background: 'rgba(255,255,255,0.06)', color: '#93c5fd', border: '1px solid rgba(255,255,255,0.1)' }
                  }
                  aria-pressed={isActive}
                >
                  <Icon className="w-4 h-4" />
                  {p.title}
                </button>
              );
            })}
          </div>

          {/* Card do produto ativo */}
          <div
            className="rounded-2xl overflow-hidden border"
            style={{ borderColor: `${DATEN_BLUE}40`, background: 'rgba(29,53,87,0.5)', backdropFilter: 'blur(12px)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div
                className="flex items-center justify-center p-6 sm:p-10"
                style={{ background: `linear-gradient(135deg, ${DATEN_NAVY} 0%, #0d1b2a 100%)` }}
              >
                <div className="text-center">
                  <img
                    src={active.image}
                    alt={`${active.title} DATEN — cotação pela Fernando Tech em Ribeirão Preto`}
                    loading="lazy"
                    className="max-h-52 w-auto object-contain mx-auto mb-4 drop-shadow-2xl"
                    onError={e => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden items-center justify-center w-40 h-40 mx-auto rounded-2xl"
                    style={{ background: `${DATEN_BLUE}20`, border: `1px solid ${DATEN_BLUE}40` }}>
                    <ActiveIcon className="w-20 h-20 opacity-40" style={{ color: DATEN_BLUE }} />
                  </div>
                  <span
                    className="inline-block text-xs sm:text-sm font-bold px-4 py-1 rounded-full mt-2"
                    style={{ background: `${DATEN_BLUE}30`, color: '#93c5fd', border: `1px solid ${DATEN_BLUE}50` }}
                  >
                    {active.subtitle}
                  </span>
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <h4 className="text-xl sm:text-2xl font-bold text-white mb-1">
                  {active.title} DATEN
                </h4>
                <p className="text-blue-300 text-sm mb-3">{active.shortDesc}</p>
                <p className="text-blue-100 text-sm mb-5">
                  <BadgeCheck className="inline w-4 h-4 mr-1" style={{ color: '#4ade80' }} />
                  <strong>Benefício:</strong> {active.benefit}
                </p>
                <p className="text-blue-300/70 text-[11px] mb-5">
                  Fonte oficial:{' '}
                  <a
                    href={active.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('daten_official_product_click', { category: active.id })}
                    className="underline hover:text-blue-200"
                  >
                    daten.com.br
                  </a>
                </p>

                <div className="flex flex-wrap gap-3">
                  <a
                    href={buildWA(WA_MSGS.category(active.title))}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('daten_whatsapp_quote', { category: active.id, location: 'product_card' })}
                    className="inline-flex items-center gap-2 font-bold px-5 py-2.5 rounded-xl text-sm text-white transition-all hover:opacity-90"
                    style={{ background: '#25D366' }}
                    data-testid={`daten-cta-quote-${active.id}`}
                  >
                    <MessageCircle className="w-4 h-4" /> Solicitar cotação
                  </a>
                  <a
                    href={active.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('daten_official_product_click', { category: active.id, location: 'product_card' })}
                    className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#93c5fd', border: `1px solid ${DATEN_BLUE}60` }}
                  >
                    Ver na DATEN <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* CTA 2 — depois dos produtos */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <CTAButton
              msgKey="products"
              label="Conhecer produtos DATEN"
              icon={MessageCircle}
              eventName="daten_whatsapp_products"
              extraParams={{ location: 'products' }}
              testId="daten-cta-products-mid"
            />
            <CTAButton
              msgKey="specialist"
              label="Falar com um especialista"
              icon={Phone}
              eventName="daten_whatsapp_specialist"
              extraParams={{ location: 'products' }}
              testId="daten-cta-specialist-mid"
              variant="secondary"
            />
          </div>
        </section>

        {/* ── NOVIDADES E INFORMAÇÕES DATEN ───────────────────────── */}
        <section aria-labelledby="daten-news-heading" className="mb-12">
          <h3
            id="daten-news-heading"
            className="text-center text-white text-lg sm:text-xl font-bold mb-6"
          >
            Novidades e informações DATEN
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {NEWS.map(n => {
              const Icon = n.icon;
              return (
                <article
                  key={n.id}
                  className={`rounded-2xl p-5 border transition-all ${n.highlight ? 'ring-2 ring-amber-400/40' : ''}`}
                  style={{
                    background: n.highlight ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255,255,255,0.04)',
                    borderColor: n.highlight ? 'rgba(245, 158, 11, 0.35)' : `${DATEN_BLUE}30`,
                  }}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{
                        background: n.highlight ? 'rgba(245, 158, 11, 0.15)' : `${DATEN_BLUE}25`,
                      }}
                    >
                      <Icon className={`w-5 h-5 ${n.highlight ? 'text-amber-400' : ''}`} style={n.highlight ? {} : { color: '#93c5fd' }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-bold text-sm leading-tight mb-1">{n.title}</h4>
                      <p className="text-[11px] text-blue-300/60">{n.date}</p>
                    </div>
                  </div>
                  <p className="text-blue-100/85 text-xs leading-relaxed mb-3">{n.excerpt}</p>
                  <a
                    href={n.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('daten_official_product_click', { news: n.id })}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-300 hover:text-white transition-colors"
                  >
                    Ver conteúdo oficial <ExternalLink className="w-3 h-3" />
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        {/* ── FAQ visível ─────────────────────────────────────────── */}
        <section aria-labelledby="daten-faq-heading" className="mb-12 max-w-3xl mx-auto">
          <h3
            id="daten-faq-heading"
            className="text-center text-white text-lg sm:text-xl font-bold mb-6"
          >
            Perguntas frequentes sobre DATEN
          </h3>
          <div className="space-y-2">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className="rounded-xl border overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.04)', borderColor: `${DATEN_BLUE}30` }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-white font-semibold text-sm hover:bg-white/5"
                    aria-expanded={isOpen}
                    aria-controls={`daten-faq-a-${i}`}
                    data-testid={`daten-faq-q-${i}`}
                  >
                    <span>{f.q}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
                  </button>
                  {isOpen && (
                    <div id={`daten-faq-a-${i}`} className="px-4 pb-4 text-blue-100/85 text-sm leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── CTA 3 — final da seção ──────────────────────────────── */}
        <div className="text-center rounded-2xl p-6 sm:p-8 border" style={{ background: 'rgba(37, 211, 102, 0.05)', borderColor: 'rgba(37, 211, 102, 0.25)' }}>
          <p className="text-white text-base sm:text-lg font-bold mb-1">
            Pronto para cotar ou levar seu equipamento DATEN à assistência?
          </p>
          <p className="text-blue-200 text-xs sm:text-sm mb-5">
            Fale agora com nossa equipe pelo WhatsApp — atendimento humano e rápido.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <CTAButton
              msgKey="quote"
              label="Cotar computador DATEN"
              icon={MessageCircle}
              eventName="daten_whatsapp_quote"
              extraParams={{ location: 'footer' }}
              testId="daten-cta-quote-footer"
            />
            <CTAButton
              msgKey="support"
              label="Solicitar assistência DATEN"
              icon={Wrench}
              eventName="daten_whatsapp_support"
              extraParams={{ location: 'footer' }}
              testId="daten-cta-support-footer"
              variant="secondary"
            />
          </div>
        </div>

        {/* Rodapé da seção — direitos e transparência */}
        <p className="text-center text-blue-300/40 text-[11px] mt-8">
          DATEN é marca registrada de seus respectivos proprietários. A Fernando Tech atua como assistência
          técnica autorizada independente. Para informações oficiais e garantia consulte{' '}
          <a
            href="https://www.daten.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-blue-300/70"
          >
            www.daten.com.br
          </a>.
        </p>
      </div>
    </section>
  );
};

export default Daten;
